import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

function isIos() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

export function useAdminPush(token) {
  const [phase, setPhase] = useState('loading');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (
      !token
      || typeof window === 'undefined'
      || !('Notification' in window)
      || !('serviceWorker' in navigator)
      || !('PushManager' in window)
    ) {
      setPhase('unsupported');
      return;
    }
    if (isIos() && !isStandalone()) {
      setPhase('ios-install');
      return;
    }
    if (Notification.permission === 'denied') {
      setPhase('denied');
      return;
    }
    try {
      const keyRes = await axios.get(`${BACKEND_URL}/api/admin/outreach/push/public-key`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!keyRes.data?.public_key) {
        setPhase('unconfigured');
        return;
      }
      const reg = await navigator.serviceWorker.getRegistration('/admin');
      const existing = reg ? await reg.pushManager.getSubscription() : null;
      setPhase(existing ? 'on' : 'off');
    } catch (err) {
      setPhase(err.response?.status === 503 ? 'unconfigured' : 'off');
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    if (!token) return;
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setPhase(permission === 'denied' ? 'denied' : 'off');
        return;
      }
      const keyRes = await axios.get(`${BACKEND_URL}/api/admin/outreach/push/public-key`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let reg = await navigator.serviceWorker.getRegistration('/admin');
      if (!reg) {
        reg = await navigator.serviceWorker.register('/admin-sw.js', { scope: '/admin' });
      }
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyRes.data.public_key),
      });
      const json = subscription.toJSON();
      await axios.post(
        `${BACKEND_URL}/api/admin/outreach/push/subscription`,
        { endpoint: json.endpoint, keys: json.keys },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPhase('on');
    } catch (err) {
      setPhase(err.response?.status === 503 ? 'unconfigured' : 'error');
    } finally {
      setBusy(false);
    }
  }, [token]);

  return { phase, busy, enable };
}
